import type { CalculationResult } from "../engine";

const mxn = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  minimumFractionDigits: 2,
});

const categoryLabels: Record<string, string> = {
  earning: "Percepciones",
  benefit: "Prestaciones",
  indemnity: "Indemnizaciones",
  litigation: "Escenario litigioso",
  tax: "Impuestos",
  deduction: "Deducciones",
  employer: "Costo patronal",
};

export function ResultPanel({
  result,
  onSave,
  saving,
}: {
  result: CalculationResult;
  onSave?: () => void;
  saving?: boolean;
}) {
  const groups = result.lines.reduce<
    Record<string, CalculationResult["lines"]>
  >((accumulator, line) => {
    (accumulator[line.category] ??= []).push(line);
    return accumulator;
  }, {});
  return (
    <section className="result-card" aria-live="polite" id="resultado">
      <header className="result-card__header">
        <div>
          <span className="eyebrow">Resultado estimado</span>
          <h2>{result.title}</h2>
          <p className="result-card__version">
            Matriz normativa {result.rulesVersion}
          </p>
        </div>
        <div className="result-card__actions no-print">
          {onSave && (
            <button
              className="button button--outline"
              onClick={onSave}
              disabled={saving}
            >
              {saving ? "Guardando…" : "Guardar cálculo"}
            </button>
          )}
          <button className="button button--dark" onClick={() => window.print()}>
            Imprimir / guardar PDF
          </button>
        </div>
      </header>

      <div className="result-total">
        <span>Total neto estimado</span>
        <strong>{mxn.format(result.totals.net)}</strong>
        <small>
          Bruto {mxn.format(result.totals.gross)} · ISR{" "}
          {mxn.format(result.totals.isr)} · IMSS{" "}
          {mxn.format(result.totals.socialSecurity)}
        </small>
      </div>

      <dl className="result-metrics">
        <div>
          <dt>Salario diario</dt>
          <dd>{mxn.format(result.salary.daily)}</dd>
        </div>
        <div>
          <dt>Salario diario integrado</dt>
          <dd>{mxn.format(result.salary.integratedDaily)}</dd>
        </div>
        <div>
          <dt>Parte exenta</dt>
          <dd>{mxn.format(result.totals.exempt)}</dd>
        </div>
        <div>
          <dt>Parte gravada</dt>
          <dd>{mxn.format(result.totals.taxable)}</dd>
        </div>
      </dl>

      <div className="result-groups">
        {Object.entries(groups).map(([category, lines]) => (
          <section key={category} className="result-group">
            <h3>{categoryLabels[category] || category}</h3>
            <div className="result-lines">
              {lines.map((line) => (
                <div className="result-line" key={line.key}>
                  <div>
                    <span>{line.label}</span>
                    {line.legalBasis && <small>{line.legalBasis}</small>}
                    {line.note && <small>{line.note}</small>}
                  </div>
                  <strong className={line.amount < 0 ? "negative" : ""}>
                    {mxn.format(line.amount)}
                  </strong>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="result-notes">
        <section>
          <h3>Supuestos usados</h3>
          <ul>
            {result.assumptions.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
        <section className="result-warning">
          <h3>Antes de tomar una decisión</h3>
          <ul>
            {result.warnings.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      </div>
      <p className="print-disclaimer">
        Resultado informativo generado por LegalFlow. No sustituye un recibo
        CFDI, una determinación del SUA, un convenio ratificado ni asesoría sobre
        un expediente concreto.
      </p>
    </section>
  );
}
